import type { PaymentPeriod } from '@kimuno/api-sdk';
import { Alert, Box, MenuItem, Select, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { type Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import { paymentRhythmDisplayNames } from '../../constants';
import { getSubscription } from '../../reducers/user.selectors';
import type { ChangeRhythmPayload } from './PaymentInfo';

interface PaymentRhythmProps {
	isEditMode: boolean;
	isShowAlert: boolean;
	availablePaymentRhythm: PaymentPeriod[];
	currentPaymentRhythm: PaymentPeriod;
	control: Control<ChangeRhythmPayload>;
}
const PaymentRhythm = ({
	isEditMode,
	isShowAlert,
	availablePaymentRhythm,
	currentPaymentRhythm,
	control,
}: PaymentRhythmProps) => {
	const { t } = useTranslation();
	const subscription = useSelector(getSubscription);
	const [selectedPaymentRhythm, setSelectedPaymentRhythm] =
		useState<PaymentPeriod>(currentPaymentRhythm);
	const onChangeRhythm = (rhythm: PaymentPeriod) => setSelectedPaymentRhythm(rhythm);

	// TODO: FD-2519: remove the promoCode condition
	const canEdit = useMemo(() => {
		return (
			subscription?.status === 'Active' &&
			!subscription?.trial?.isInTrial &&
			!subscription?.paymentPeriod?.following &&
			!subscription?.promoCode
		);
	}, [
		subscription?.status,
		subscription?.trial?.isInTrial,
		subscription?.paymentPeriod.following,
		subscription?.promoCode,
	]);

	return isEditMode && canEdit ? (
		<Controller
			name="paymentRhythm"
			control={control}
			defaultValue={selectedPaymentRhythm}
			as={
				<Select
					sx={{ width: '100%', height: '40px', borderRadius: borderRadius.sm }}
					onChange={(e) => onChangeRhythm(e.target.value as any)}
				>
					{availablePaymentRhythm.map((rhythm) => (
						<MenuItem key={rhythm} value={rhythm}>
							{t(paymentRhythmDisplayNames[rhythm])}
						</MenuItem>
					))}
				</Select>
			}
		/>
	) : (
		<Box sx={{ gap: spaces.sm }}>
			<Typography>{t(paymentRhythmDisplayNames[currentPaymentRhythm as PaymentPeriod])}</Typography>
			{isShowAlert && (
				<Alert
					severity="info"
					variant="outlined"
					sx={{
						borderColor: palette.info.main,
						color: palette.info.dark,
						paddingX: spaces.md,
						paddingY: spaces.sm,
						marginTop: spaces.lg,
						fontFamily: 'Poppins',
						fontSize: '14px',
						fontStyle: 'normal',
						fontWeight: 300,
						lineHeight: '20px',
						letterSpacing: '0.25px',
					}}
				>
					{t('We have received your payment rhythm change request.')}
				</Alert>
			)}
		</Box>
	);
};

export default PaymentRhythm;
